'use client';
import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { api } from '@/lib/api';
import { useT } from '@/lib/useT';
import { Search, X, Building2, Users, Truck, Package, Command } from 'lucide-react';

interface SearchResult {
  orgs: { id: string; name: string; slug: string; plan: string; isActive: boolean }[];
  users: { id: string; name: string; surname: string; email: string | null; role: string;
           memberships: { org: { name: string; slug: string } }[] }[];
  vehicles: { id: string; plateNumber: string; model: string; year: number | null;
              org: { id: string; name: string; slug: string } | null }[];
  orders: { id: string; publicId: string; customerName: string; status: string;
            deliveryAddress: string; org: { name: string; slug: string } | null }[];
}

export function GlobalSearch() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [res, setRes] = useState<SearchResult | null>(null);
  const [busy, setBusy] = useState(false);
  const debRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const search = useCallback(async (query: string) => {
    if (query.trim().length < 2) { setRes(null); return; }
    setBusy(true);
    try {
      const r = await api.get(`/super-admin/search?q=${encodeURIComponent(query)}`);
      setRes(r);
    } catch {
      setRes(null);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (debRef.current) window.clearTimeout(debRef.current);
    debRef.current = window.setTimeout(() => search(q), 200);
  }, [q, search]);

  const closeAndGo = (path: string) => { setOpen(false); router.push(path); };

  const totalHits = res ? res.orgs.length + res.users.length + res.vehicles.length + res.orders.length : 0;

  return (
    <>
      {/* Trigger pill (top-right of super-admin header) */}
      <button onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs"
        style={{ background: 'var(--c-card)', color: 'var(--c-muted)', border: '1px solid var(--c-border)' }}>
        <Search size={12} />
        <span>{t('search')}</span>
        <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-mono"
          style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)' }}>⌘K</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center pt-24" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={() => setOpen(false)}>
          <div className="w-full max-w-xl rounded-2xl overflow-hidden"
            style={{ background: 'var(--c-panel)', border: '1px solid var(--c-border)' }}
            onClick={e => e.stopPropagation()}>
            {/* Input */}
            <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: '1px solid var(--c-border)' }}>
              <Search size={16} style={{ color: 'var(--c-muted)' }} />
              <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
                placeholder={t('sa_gsearch_ph')}
                className="flex-1 bg-transparent text-sm outline-none"
                style={{ color: 'var(--c-heading)' }} />
              {busy && <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"
                style={{ color: 'var(--c-accent)' }} />}
              <button onClick={() => setOpen(false)} className="p-1 opacity-50 hover:opacity-100">
                <X size={14} style={{ color: 'var(--c-muted)' }} />
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[60vh] overflow-y-auto p-2">
              {!res && q.trim().length < 2 && (
                <div className="px-3 py-8 text-center text-xs" style={{ color: 'var(--c-muted)' }}>
                  {t('sa_gsearch_min_chars')}<br />
                  {t('sa_gsearch_tip')}
                </div>
              )}

              {res && totalHits === 0 && !busy && (
                <div className="px-3 py-8 text-center text-xs" style={{ color: 'var(--c-muted)' }}>
                  {t('sa_gsearch_no_hits').replace('{q}', q)}
                </div>
              )}

              {res && (
                <>
                  {res.orgs.length > 0 && (
                    <Group title={t('nav_organizations')} icon={Building2}>
                      {res.orgs.map(o => (
                        <ResultRow key={o.id} onClick={() => closeAndGo('/super-admin/organizations')}>
                          <strong>{o.name}</strong>
                          <span className="text-[10px] ml-2" style={{ color: 'var(--c-muted)' }}>{o.slug} · {o.plan}</span>
                          {!o.isActive && <span className="ml-2 px-1.5 rounded text-[10px]" style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}>{t('sa_gsearch_inactive')}</span>}
                        </ResultRow>
                      ))}
                    </Group>
                  )}
                  {res.users.length > 0 && (
                    <Group title={t('sa_gsearch_users')} icon={Users}>
                      {res.users.map(u => (
                        <ResultRow key={u.id} onClick={() => closeAndGo('/super-admin/users')}>
                          <strong>{u.name} {u.surname}</strong>
                          <span className="text-[10px] ml-2" style={{ color: 'var(--c-muted)' }}>{u.email || '—'} · {u.role}</span>
                          {u.memberships?.[0] && <span className="ml-2 text-[10px]" style={{ color: 'var(--c-muted)' }}>@ {u.memberships[0].org.name}</span>}
                        </ResultRow>
                      ))}
                    </Group>
                  )}
                  {res.vehicles.length > 0 && (
                    <Group title={t('fleet_vehicles_h')} icon={Truck}>
                      {res.vehicles.map(v => (
                        <ResultRow key={v.id} onClick={() => closeAndGo('/super-admin/organizations')}>
                          <strong>{v.plateNumber}</strong>
                          <span className="text-[10px] ml-2" style={{ color: 'var(--c-muted)' }}>{v.model} {v.year || ''}</span>
                          {v.org && <span className="ml-2 text-[10px]" style={{ color: 'var(--c-muted)' }}>@ {v.org.name}</span>}
                        </ResultRow>
                      ))}
                    </Group>
                  )}
                  {res.orders.length > 0 && (
                    <Group title={t('nav_orders')} icon={Package}>
                      {res.orders.map(o => (
                        <ResultRow key={o.id} onClick={() => closeAndGo('/super-admin/organizations')}>
                          <strong>{o.customerName}</strong>
                          <span className="text-[10px] ml-2" style={{ color: 'var(--c-muted)' }}>#{o.publicId.slice(0, 8)} · {o.status}</span>
                          {o.org && <span className="ml-2 text-[10px]" style={{ color: 'var(--c-muted)' }}>@ {o.org.name}</span>}
                        </ResultRow>
                      ))}
                    </Group>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center justify-between px-4 py-2 text-[10px]"
              style={{ borderTop: '1px solid var(--c-border)', color: 'var(--c-muted)' }}>
              <div className="flex items-center gap-3">
                <span><Command size={9} className="inline" /> {t('sa_gsearch_k_open')}</span>
                <span>{t('sa_gsearch_esc_close')}</span>
              </div>
              <span>{totalHits > 0 ? t('sa_gsearch_hits').replace('{n}', String(totalHits)) : ''}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Group({ title, icon: Icon, children }: { title: string; icon: any; children: React.ReactNode }) {
  return (
    <div className="mb-2">
      <div className="flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-wider font-semibold"
        style={{ color: 'var(--c-muted)' }}>
        <Icon size={10} />
        {title}
      </div>
      {children}
    </div>
  );
}

function ResultRow({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors"
      style={{ color: 'var(--c-text)' }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-hover)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
      {children}
    </button>
  );
}
